<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isLoading">

    <app-page-hero
        eyebrow="Shipment Intake"
        title="Create Shipment Plan"
        helpManualId="shipment-create"
        [helpChecklist]="helpChecklist"
        subtitle="Plan parties, route, cargo, equipment, commercial terms, and readiness before dispatch.">
        <div actions>
            <p-button label="Express shipment" icon="pi pi-bolt" [outlined]="true" severity="secondary" (onClick)="switchToExpressShipment()"></p-button>
            <p-button label="Clear" icon="fa-solid fa-xmark" [outlined]="true" severity="secondary" (onClick)="clearShipmentForm()"></p-button>
            <p-button label="Create Shipment & Assign Drivers" icon="fa-solid fa-truck-fast" [outlined]="true" severity="secondary"
                [loading]="shipmentCreationInProgress" (onClick)="submitOrder(true)" [disabled]="!canSubmitIntake()"></p-button>
            <p-button label="Create Shipment" icon="fa-solid fa-plus" [loading]="shipmentCreationInProgress"
                (onClick)="submitOrder()" [disabled]="!canSubmitIntake()"></p-button>
        </div>
    </app-page-hero>

    @if (isExecutionLocked()) {
    <div class="execution-lock-banner">
        <i class="fa-solid fa-lock"></i>
        <span>Route, cargo, and equipment fields are locked because execution records already exist for this shipment.</span>
    </div>
    }

    <form [formGroup]="createShipmentForm" novalidate autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="off">
        <div class="shipment-intake-layout">
            <div class="shipment-intake-main">
                <app-page-form-section
                    icon="fa-solid fa-file-signature"
                    title="Shipment Header"
                    subtitle="Set ownership, customer, references, schedule, route type, and priority.">
                    <div class="formgrid grid">
                        <div class="field col-12">
                            <app-commercial-party-picker
                                [owningCompanyId]="owningCompanyId"
                                [value]="commercialPartyReference"
                                [required]="true"
                                label="Bill-to Commercial Party"
                                (valueChange)="onCommercialPartyChange($event)">
                            </app-commercial-party-picker>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <app-logistics-project-lookup inputId="logistics-project" [(ngModel)]="shipment.logisticsProjectId"
                                        [ngModelOptions]="{standalone: true}"></app-logistics-project-lookup>
                                    <label for="logistics-project">Logistics Project</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-tags"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <input id="reference" pInputText [(ngModel)]="shipmentReference" [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()" />
                                    <label for="reference">Reference</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-flag"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="priority" [options]="priorityOptions" [(ngModel)]="priority" [ngModelOptions]="{standalone: true}"
                                        optionLabel="label" optionValue="value" (onChange)="refreshIntakePreviews()" styleClass="w-full">
                                    </p-select>
                                    <label for="priority">Priority</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-route"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="route-type" [options]="routeTypes" formControlName="routeTypeDropdown"
                                        optionLabel="label" optionValue="value" (onChange)="refreshIntakePreviews()" styleClass="w-full">
                                    </p-select>
                                    <label for="route-type">Route Type</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6 xl:col-4">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-truck-ramp-box"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="service-category" [options]="serviceCategories" formControlName="serviceCategoryDropdown"
                                        optionLabel="label" optionValue="value" (onChange)="refreshIntakePreviews()" styleClass="w-full">
                                    </p-select>
                                    <label for="service-category">Service Category</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-calendar-alt"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-datePicker id="pickupDate" [(ngModel)]="pickupDate" [showTime]="true"
                                    (ngModelChange)="refreshContractPricingDecision(); refreshIntakePreviews()" [showIcon]="false" formControlName="pickupDateSelect" styleClass="w-full">
                                    </p-datePicker>
                                    <label for="pickupDate">Pickup Window</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-datePicker id="deliveryWindowEnd" [(ngModel)]="deliveryWindowEnd" [showTime]="true"
                                        (ngModelChange)="refreshIntakePreviews()" [showIcon]="false" [minDate]="pickupDate ?? undefined" [ngModelOptions]="{standalone: true}" styleClass="w-full">
                                    </p-datePicker>
                                    <label for="deliveryWindowEnd">Delivery Window</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                            @if (hasInvalidDeliveryWindow()) {
                                <small class="p-error block mt-1">Delivery window must be on or after the pickup window.</small>
                            }
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="fa-solid fa-people-carry-box"
                    title="Workforce Requirements"
                    subtitle="Optional roles are copied to every delivery. Choose people later during delivery assignment if needed.">
                    <div actions>
                        <p-button label="Add labor role" icon="pi pi-plus" severity="secondary" [outlined]="true"
                            (onClick)="addWorkforceRequirement()"></p-button>
                    </div>
                    @if (!workforceRequirements.length) {
                    <div class="text-color-secondary text-sm">No labor is required for this shipment.</div>
                    }
                    @for (requirement of workforceRequirements; track $index) {
                    <div class="formgrid grid align-items-end border-bottom-1 surface-border py-3">
                        <div class="field col-12 md:col-4">
                            <p-iftaLabel>
                                <p-select [inputId]="'workforce-role-' + $index" [options]="workforceRoles"
                                    [(ngModel)]="requirement.fieldRoleId" [ngModelOptions]="{standalone: true}"
                                    optionLabel="roleName" optionValue="fieldRoleId" [filter]="true" [showClear]="true"
                                    (onChange)="workforceRequirementChanged()" styleClass="w-full">
                                </p-select>
                                <label [for]="'workforce-role-' + $index">Labor role</label>
                            </p-iftaLabel>
                        </div>
                        <div class="field col-6 md:col-2">
                            <p-iftaLabel>
                                <p-inputNumber [inputId]="'workforce-headcount-' + $index" [(ngModel)]="requirement.headcount"
                                    [ngModelOptions]="{standalone: true}" [min]="1" [useGrouping]="false"
                                    (ngModelChange)="workforceRequirementChanged()" styleClass="w-full"></p-inputNumber>
                                <label [for]="'workforce-headcount-' + $index">People</label>
                            </p-iftaLabel>
                        </div>
                        <div class="field col-6 md:col-2">
                            <p-iftaLabel>
                                <p-inputNumber [inputId]="'workforce-sell-rate-' + $index" [(ngModel)]="requirement.sellRate"
                                    [ngModelOptions]="{standalone: true}" [min]="0" mode="currency"
                                    [currency]="requirement.currencyCode || currencyCode" (ngModelChange)="workforceRequirementChanged()"
                                    styleClass="w-full"></p-inputNumber>
                                <label [for]="'workforce-sell-rate-' + $index">Sell rate (optional)</label>
                            </p-iftaLabel>
                        </div>
                        <div class="field col-6 md:col-2">
                            <p-iftaLabel>
                                <p-select [inputId]="'workforce-unit-' + $index" [options]="workforceBillingUnits"
                                    [(ngModel)]="requirement.billingUnit" [ngModelOptions]="{standalone: true}"
                                    (onChange)="workforceRequirementChanged()" styleClass="w-full"></p-select>
                                <label [for]="'workforce-unit-' + $index">Billing unit</label>
                            </p-iftaLabel>
                        </div>
                        <div class="field col-6 md:col-1">
                            <p-iftaLabel>
                                <p-select [inputId]="'workforce-currency-' + $index" [options]="currencyOptions"
                                    [(ngModel)]="requirement.currencyCode" [ngModelOptions]="{standalone: true}"
                                    optionLabel="label" optionValue="value" (onChange)="workforceRequirementChanged()" styleClass="w-full"></p-select>
                                <label [for]="'workforce-currency-' + $index">Currency</label>
                            </p-iftaLabel>
                        </div>
                        <div class="field col-12 md:col-1 text-right">
                            <p-button icon="pi pi-trash" severity="danger" [text]="true" ariaLabel="Remove labor role"
                                (onClick)="removeWorkforceRequirement($index)"></p-button>
                        </div>
                    </div>
                    }
                </app-page-form-section>

                <app-page-form-section
                    icon="fa-solid fa-map-location-dot"
                    title="Route & Contacts"
                    subtitle="Capture each location with the person who will handle it. Pickup starts the route, optional stops stay in the middle, and drop-off ends it.">
                    <div class="route-contact-row route-contact-row--source">
                        <div class="route-contact-row__marker">
                            <span>1</span>
                            <strong>Pickup</strong>
                        </div>
                        <div class="formgrid grid route-contact-row__fields">
                            <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-location-arrow"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="source-location" [options]="locationOptions()" [(ngModel)]="shipment.sourceLocation"
                                        (onChange)="onLocationSelect('source')" optionLabel="name" dataKey="id" formControlName="sourceLocationDropdown"
                                        [filter]="true" [showClear]="true" [loading]="locationOptionsLoading" [optionDisabled]="'locationLoading'"
                                        [lazy]="true" [virtualScroll]="true" [virtualScrollItemSize]="38"
                                        [virtualScrollOptions]="{ showLoader: true, loading: locationOptionsLoading, delay: 0 }"
                                        (onLazyLoad)="onLocationPickerLazyLoad($event)" [disabled]="isExecutionLocked()" styleClass="w-full">
                                        <ng-template #filter>
                                            <input pInputText type="text" class="w-full" autocomplete="off"
                                                placeholder="Search locations by name" [value]="locationSearchText"
                                                (input)="onLocationFilter($any($event.target).value)" (keydown)="$event.stopPropagation()">
                                        </ng-template>
                                        <ng-template #item let-location>
                                            @if (isLocationLoadingOption(location)) {
                                                <div class="flex align-items-center gap-2 text-color-secondary">
                                                    <i class="pi pi-spin pi-spinner"></i><small>Loading location…</small>
                                                </div>
                                            } @else if (location) {
                                                <span>{{ location.name }}</span>
                                            }
                                        </ng-template>
                                    </p-select>
                                    <label for="source-location">Source Location</label>
                                </p-iftaLabel>
                                <p-inputGroupAddon><p-button icon="pi pi-plus" (onClick)="openLocationDialog('source')" severity="secondary"/></p-inputGroupAddon>
                            </p-inputGroup>
                        </div>
                            <div class="field col-12 md:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select id="sender-contact" [options]="partyCustomerOptions()" [(ngModel)]="selectedSenderContact"
                                            [ngModelOptions]="{standalone: true}" optionLabel="name" dataKey="id" [filter]="true"
                                            [showClear]="true" [loading]="partyCustomerOptionsLoading" [optionDisabled]="'partyCustomerLoading'" [lazy]="true" [virtualScroll]="true" [virtualScrollItemSize]="52"
                                            [virtualScrollOptions]="{ showLoader: true, loading: partyCustomerOptionsLoading, delay: 0 }"
                                            (onShow)="onPartyCustomerPickerShown()" (onHide)="onPartyCustomerPickerHidden()" (onLazyLoad)="onPartyCustomerPickerLazyLoad($event)"
                                            (ngModelChange)="onPartyCustomerSelected($event)" [disabled]="isExecutionLocked()" styleClass="w-full">
                                            <ng-template #filter>
                                                <input pInputText type="text" class="w-full" autocomplete="off"
                                                    placeholder="Search all Party Customers by name, email, or phone"
                                                    [value]="partyCustomerSearchText" (input)="onPartyCustomerFilter($any($event.target).value)" (keydown)="$event.stopPropagation()">
                                            </ng-template>
                                            <ng-template #item let-contact>
                                                @if (isPartyCustomerLoadingOption(contact)) {
                                                    <div class="flex align-items-center gap-2 text-color-secondary">
                                                        <i class="pi pi-spin pi-spinner"></i>
                                                        <small>Loading contact...</small>
                                                    </div>
                                                } @else if (contact) {
                                                    <div class="flex flex-column gap-1">
                                                        <strong>{{ contact.name }}</strong>
                                                        <small>{{ contact.emailAddress || contact.phoneNumber || contact.address || 'Party Customer' }}</small>
                                                    </div>
                                                }
                                            </ng-template>
                                        </p-select>
                                        <label for="sender-contact">Sender — Party Customer (optional)</label>
                                    </p-iftaLabel>
                                    <p-inputGroupAddon><p-button icon="pi pi-plus" severity="secondary" [outlined]="true" ariaLabel="Add pickup contact" (onClick)="registerContact('sender')" [disabled]="isExecutionLocked()" /></p-inputGroupAddon>
                                </p-inputGroup>
                            </div>
                        </div>
                    </div>

                    <div class="route-preview-strip">
                        <div><span>Estimated Distance</span><strong>{{ routePreview?.distanceKm ? (routePreview?.distanceKm + ' km') : 'Pending' }}</strong></div>
                        <div><span>Estimated Duration</span><strong>{{ formatDurationHours(routePreview?.durationSeconds) }}</strong></div>
                        <div><span>Preview</span><strong>{{ routePreview?.message || 'Select source and destination.' }}</strong></div>
                    </div>

                    <div class="section-toolbar route-contact-toolbar">
                        <div>
                            <strong>Additional Route Stops</strong>
                            <small>Optional locations between pickup and drop-off, with their operational contact.</small>
                        </div>
                        <p-button label="Add Intermediate Stop" icon="fa-solid fa-plus" severity="secondary" [outlined]="true" (onClick)="addRouteStop()" [disabled]="isExecutionLocked()"></p-button>
                    </div>
                    @for (stop of routeStops; track $index) {
                    <div class="route-contact-row route-contact-row--middle">
                        <div class="route-contact-row__marker">
                            <span>{{ $index + 2 }}</span>
                            <strong>Stop</strong>
                        </div>
                        <div class="formgrid grid route-contact-row__fields">
                            <div class="field col-12 md:col-5">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-location-dot"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select [id]="'stop-location-' + $index" [options]="locationOptions()" [(ngModel)]="stop.location"
                                            [ngModelOptions]="{standalone: true}" optionLabel="name" dataKey="id" [filter]="true" [showClear]="true"
                                            [loading]="locationOptionsLoading" [optionDisabled]="'locationLoading'" [lazy]="true" [virtualScroll]="true"
                                            [virtualScrollItemSize]="38" [virtualScrollOptions]="{ showLoader: true, loading: locationOptionsLoading, delay: 0 }"
                                            (onLazyLoad)="onLocationPickerLazyLoad($event)" (ngModelChange)="onStopLocationSelected(stop)" [disabled]="isExecutionLocked()" styleClass="w-full">
                                            <ng-template #filter>
                                                <input pInputText type="text" class="w-full" autocomplete="off"
                                                    placeholder="Search locations by name" [value]="locationSearchText"
                                                    (input)="onLocationFilter($any($event.target).value)" (keydown)="$event.stopPropagation()">
                                            </ng-template>
                                            <ng-template #item let-location>
                                                @if (isLocationLoadingOption(location)) {
                                                    <div class="flex align-items-center gap-2 text-color-secondary">
                                                        <i class="pi pi-spin pi-spinner"></i><small>Loading location…</small>
                                                    </div>
                                                } @else if (location) {
                                                    <span>{{ location.name }}</span>
                                                }
                                            </ng-template>
                                        </p-select>
                                        <label [for]="'stop-location-' + $index">Intermediate Location</label>
                                    </p-iftaLabel>
                                    <p-inputGroupAddon><p-button icon="pi pi-plus" severity="secondary" [disabled]="isExecutionLocked()" (onClick)="openStopLocationDialog($index)"></p-button></p-inputGroupAddon>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-5">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select [id]="'stop-contact-' + $index" [options]="partyCustomerOptions()" [(ngModel)]="stop.contact"
                                            [ngModelOptions]="{standalone: true}" optionLabel="name" dataKey="id" [filter]="true"
                                            [showClear]="true" [loading]="partyCustomerOptionsLoading" [optionDisabled]="'partyCustomerLoading'" [lazy]="true" [virtualScroll]="true" [virtualScrollItemSize]="52"
                                            [virtualScrollOptions]="{ showLoader: true, loading: partyCustomerOptionsLoading, delay: 0 }"
                                            (onShow)="onPartyCustomerPickerShown()" (onHide)="onPartyCustomerPickerHidden()" (onLazyLoad)="onPartyCustomerPickerLazyLoad($event)"
                                            (ngModelChange)="onPartyCustomerSelected($event)" [disabled]="isExecutionLocked()" styleClass="w-full">
                                            <ng-template #filter>
                                                <input pInputText type="text" class="w-full" autocomplete="off"
                                                    placeholder="Search all Party Customers by name, email, or phone"
                                                    [value]="partyCustomerSearchText" (input)="onPartyCustomerFilter($any($event.target).value)" (keydown)="$event.stopPropagation()">
                                            </ng-template>
                                            <ng-template #item let-contact>
                                                @if (isPartyCustomerLoadingOption(contact)) {
                                                    <div class="flex align-items-center gap-2 text-color-secondary">
                                                        <i class="pi pi-spin pi-spinner"></i>
                                                        <small>Loading contact...</small>
                                                    </div>
                                                } @else if (contact) {
                                                    <div class="flex flex-column gap-1">
                                                        <strong>{{ contact.name }}</strong>
                                                        <small>{{ contact.emailAddress || contact.phoneNumber || contact.address || 'Party Customer' }}</small>
                                                    </div>
                                                }
                                            </ng-template>
                                        </p-select>
                                        <label [for]="'stop-contact-' + $index">Stop Contact — Party Customer (optional)</label>
                                    </p-iftaLabel>
                                    <p-inputGroupAddon><p-button icon="pi pi-plus" severity="secondary" [outlined]="true" ariaLabel="Add stop contact" (onClick)="registerStopContact($index)" [disabled]="isExecutionLocked()" /></p-inputGroupAddon>
                                </p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-2 stop-actions">
                                <p-button label="Remove" icon="pi pi-trash" severity="danger" [outlined]="true" (onClick)="removeRouteStop($index)" [disabled]="isExecutionLocked()"></p-button>
                            </div>
                        </div>
                    </div>
                    }

                    <div class="route-contact-row route-contact-row--destination">
                        <div class="route-contact-row__marker">
                            <span>{{ routeStops.length + 2 }}</span>
                            <strong>Drop-off</strong>
                        </div>
                        <div class="formgrid grid route-contact-row__fields">
                            <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-map-marker-alt"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="destination-location" [options]="locationOptions()" [(ngModel)]="shipment.destinationLocation"
                                        (onChange)="onLocationSelect('destination')" optionLabel="name" dataKey="id" formControlName="destinationLocationDropdown"
                                        [filter]="true" [showClear]="true" [loading]="locationOptionsLoading" [optionDisabled]="'locationLoading'"
                                        [lazy]="true" [virtualScroll]="true" [virtualScrollItemSize]="38"
                                        [virtualScrollOptions]="{ showLoader: true, loading: locationOptionsLoading, delay: 0 }"
                                        (onLazyLoad)="onLocationPickerLazyLoad($event)" [disabled]="isExecutionLocked()" styleClass="w-full">
                                        <ng-template #filter>
                                            <input pInputText type="text" class="w-full" autocomplete="off"
                                                placeholder="Search locations by name" [value]="locationSearchText"
                                                (input)="onLocationFilter($any($event.target).value)" (keydown)="$event.stopPropagation()">
                                        </ng-template>
                                        <ng-template #item let-location>
                                            @if (isLocationLoadingOption(location)) {
                                                <div class="flex align-items-center gap-2 text-color-secondary">
                                                    <i class="pi pi-spin pi-spinner"></i><small>Loading location…</small>
                                                </div>
                                            } @else if (location) {
                                                <span>{{ location.name }}</span>
                                            }
                                        </ng-template>
                                    </p-select>
                                    <label for="destination-location">Destination Location</label>
                                </p-iftaLabel>
                                <p-inputGroupAddon><p-button icon="pi pi-plus" (onClick)="openLocationDialog('destination')" severity="secondary"/></p-inputGroupAddon>
                            </p-inputGroup>
                        </div>
                            <div class="field col-12 md:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-user-check"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select id="receiver-contact" [options]="partyCustomerOptions()" [(ngModel)]="selectedReceiverContact"
                                            [ngModelOptions]="{standalone: true}" optionLabel="name" dataKey="id" [filter]="true"
                                            [showClear]="true" [loading]="partyCustomerOptionsLoading" [optionDisabled]="'partyCustomerLoading'" [lazy]="true" [virtualScroll]="true" [virtualScrollItemSize]="52"
                                            [virtualScrollOptions]="{ showLoader: true, loading: partyCustomerOptionsLoading, delay: 0 }"
                                            (onShow)="onPartyCustomerPickerShown()" (onHide)="onPartyCustomerPickerHidden()" (onLazyLoad)="onPartyCustomerPickerLazyLoad($event)"
                                            (ngModelChange)="onPartyCustomerSelected($event)" [disabled]="isExecutionLocked()" styleClass="w-full">
                                            <ng-template #filter>
                                                <input pInputText type="text" class="w-full" autocomplete="off"
                                                    placeholder="Search all Party Customers by name, email, or phone"
                                                    [value]="partyCustomerSearchText" (input)="onPartyCustomerFilter($any($event.target).value)" (keydown)="$event.stopPropagation()">
                                            </ng-template>
                                            <ng-template #item let-contact>
                                                @if (isPartyCustomerLoadingOption(contact)) {
                                                    <div class="flex align-items-center gap-2 text-color-secondary">
                                                        <i class="pi pi-spin pi-spinner"></i>
                                                        <small>Loading contact...</small>
                                                    </div>
                                                } @else if (contact) {
                                                    <div class="flex flex-column gap-1">
                                                        <strong>{{ contact.name }}</strong>
                                                        <small>{{ contact.emailAddress || contact.phoneNumber || contact.address || 'Party Customer' }}</small>
                                                    </div>
                                                }
                                            </ng-template>
                                        </p-select>
                                        <label for="receiver-contact">Receiver — Party Customer (optional)</label>
                                    </p-iftaLabel>
                                    <p-inputGroupAddon><p-button icon="pi pi-plus" severity="secondary" [outlined]="true" ariaLabel="Add drop-off contact" (onClick)="registerContact('receiver')" [disabled]="isExecutionLocked()" /></p-inputGroupAddon>
                                </p-inputGroup>
                            </div>
                        </div>
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="fa-solid fa-boxes-stacked"
                    title="Cargo & Load"
                    subtitle="Capture every payload line so delivery and billing receive the correct load detail.">
                    @if (hasWarehouseItems()) {
                    <div class="field col-12">
                        <label class="block mb-2 font-semibold">Warehouse item movement</label>
                        <p-selectButton [options]="warehouseFlowOptions" [(ngModel)]="shipment.warehouseFlowDirection"
                            [ngModelOptions]="{standalone: true}" optionLabel="label" optionValue="value"
                            [allowEmpty]="false" [disabled]="isExecutionLocked()"
                            (ngModelChange)="onWarehouseFlowChange()"></p-selectButton>
                        @if (shipment.warehouseFlowDirection === 'INBOUND') {
                        <small class="block mt-2">Receiving warehouse:
                            @if (inboundDestinationWarehouse(); as warehouse) { <strong>{{ warehouse.name }}</strong> }
                            @else { <strong>Choose a destination mapped to one active company warehouse.</strong> }
                            Stock enters inventory only after the destination stop and GRN receipt.
                        </small>
                        }
                    </div>
                    }
                    @for (line of shipment.payloadItemList; track $index) {
                    <div class="cargo-line-card">
                        <div class="cargo-line-header">
                            <strong>Cargo Line {{ $index + 1 }}</strong>
                            @if (shipment.payloadItemList.length > 1) {
                            <p-button icon="pi pi-trash" severity="danger" [rounded]="true" [text]="true" (onClick)="removeCargoLine($index)" [disabled]="isExecutionLocked()"></p-button>
                            }
                        </div>
                        @if (outboundPickupIssue(line); as pickupIssue) {
                        <small class="block text-red-600 mt-2" role="alert">{{ pickupIssue }}</small>
                        }
                        <div class="formgrid grid">
                            <div class="field col-12 cargo-source-field">
                                <p-selectButton [options]="cargoLineSourceOptions" [(ngModel)]="line.lineSource"
                                    [ngModelOptions]="{standalone: true}" optionLabel="label" optionValue="value"
                                    [allowEmpty]="false" [disabled]="isExecutionLocked()"
                                    (ngModelChange)="onCargoLineSourceChange(line, $index)">
                                </p-selectButton>
                            </div>

                            @if (isContainerLine(line)) {
                            <div class="field col-12">
                                <div class="container-manifest-notice">
                                    <i class="fa-solid fa-boxes-packing"></i>
                                    <span>Each container line creates one case. Leave the number blank if it is not yet known.</span>
                                </div>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-3">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'container-number-' + $index" pInputText [(ngModel)]="line.containerNumber" [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()" [disabled]="isExecutionLocked()" class="w-full" />
                                    <label [for]="'container-number-' + $index">Container Number</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field col-12">
                                <p-button label="Add Container" icon="pi pi-plus" [outlined]="true" [disabled]="isExecutionLocked()" (onClick)="addContainerLine(line)"></p-button>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-3">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-expand"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [id]="'container-type-' + $index" [options]="containerEquipmentTypeOptions"
                                        [(ngModel)]="line.isoEquipmentType" [ngModelOptions]="{standalone: true}"
                                        optionLabel="label" optionValue="value" [filter]="true" filterBy="label,value"
                                        [showClear]="true" (ngModelChange)="refreshIntakePreviews()"
                                        [disabled]="isExecutionLocked()" appendTo="body" styleClass="w-full">
                                    </p-select>
                                    <label [for]="'container-type-' + $index">Size & Type</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-3">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-lock"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'container-seal-' + $index" pInputText [(ngModel)]="line.sealNumber" [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()" [disabled]="isExecutionLocked()" class="w-full" />
                                    <label [for]="'container-seal-' + $index">Seal Number</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-3">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-calendar-check"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'container-booking-' + $index" pInputText [(ngModel)]="line.bookingNumber" [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()" [disabled]="isExecutionLocked()" class="w-full" />
                                    <label [for]="'container-booking-' + $index">Booking Number</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-5">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-route"></i></p-inputGroupAddon><p-iftaLabel>
                                    <p-select [id]="'container-journey-template-' + $index" [options]="containerJourneyTemplates"
                                        [(ngModel)]="line.journeyTemplateId" [ngModelOptions]="{standalone: true}"
                                        optionLabel="template.templateName" optionValue="template.id" [filter]="true" filterBy="template.templateName,template.templateCode"
                                        [showClear]="true" [loading]="containerJourneyTemplatesLoading" appendTo="body"
                                        (ngModelChange)="refreshIntakePreviews()" [disabled]="isExecutionLocked()" styleClass="w-full">
                                        <ng-template #item let-profile>
                                            <div class="journey-template-option">
                                                <strong>{{ profile.template?.templateName }}</strong>
                                                <small>{{ profile.template?.templateCode }} - {{ profile.items?.length || 0 }} steps</small>
                                            </div>
                                        </ng-template>
                                    </p-select>
                                    <label [for]="'container-journey-template-' + $index">Equipment Journey Plan (optional)</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-4">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-file-lines"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'container-bl-' + $index" pInputText [(ngModel)]="line.billOfLadingNumber" [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()" [disabled]="isExecutionLocked()" class="w-full" />
                                    <label [for]="'container-bl-' + $index">Bill of Lading</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-4">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-file-lines"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'container-hbl-' + $index" pInputText [(ngModel)]="line.houseBillNumber" [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()" [disabled]="isExecutionLocked()" class="w-full" />
                                    <label [for]="'container-hbl-' + $index">House Bill</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            <div class="field col-12 md:col-6 lg:col-4">
                                <p-inputGroup><p-inputGroupAddon><i class="fa-solid fa-stamp"></i></p-inputGroupAddon><p-iftaLabel>
                                    <input [id]="'container-customs-' + $index" pInputText [(ngModel)]="line.customsDeclarationNumber" [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()" [disabled]="isExecutionLocked()" class="w-full" />
                                    <label [for]="'container-customs-' + $index">Customs Declaration</label>
                                </p-iftaLabel></p-inputGroup>
                            </div>
                            } @else if (isWarehouseItemLine(line)) {
                            <div class="field col-12 lg:col-6">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-boxes-stacked"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select [id]="'warehouse-item-' + $index" [options]="warehouseItemOptions"
                                            [(ngModel)]="line.inventoryItemId" [ngModelOptions]="{standalone: true}"
                                            optionValue="inventoryItemId" [filter]="true"
                                            filterBy="itemName,itemNumber,skuNumber,customerItemNumber,itemDescription"
                                            [showClear]="true" [loading]="warehouseItemsLoading" appendTo="body"
                                            [disabled]="isExecutionLocked()" (ngModelChange)="onWarehouseItemChange(line, $index)"
                                            styleClass="w-full">
                                            <ng-template #selectedItem let-item>
                                                @if (item) {
                                                <span>{{ item.itemName }} | {{ item.skuNumber || item.itemNumber }}</span>
                                                }
                                            </ng-template>
                                            <ng-template #item let-item>
                                                <div class="warehouse-item-option">
                                                    <strong>{{ item.itemName }}</strong>
                                                    <span>{{ item.skuNumber || item.itemNumber }}@if (item.customerItemNumber) { | Customer {{ item.customerItemNumber }}}</span>
                                                </div>
                                            </ng-template>
                                        </p-select>
                                        <label [for]="'warehouse-item-' + $index">Warehouse Item / SKU</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                                @if (warehouseItemsMessage) {
                                <small class="warehouse-item-message">{{ warehouseItemsMessage }}</small>
                                }
                            </div>
                            @if (shipment.warehouseFlowDirection !== 'INBOUND') {
                            <div class="field col-12 md:col-6 lg:col-3">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-warehouse"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select [id]="'source-warehouse-' + $index" [options]="warehouseOptions"
                                            [(ngModel)]="line.sourceWarehouseId" [ngModelOptions]="{standalone: true}"
                                            optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code"
                                            [showClear]="true" appendTo="body" (ngModelChange)="refreshIntakePreviews()"
                                            [disabled]="isExecutionLocked() || !line.inventoryItemId" styleClass="w-full">
                                        </p-select>
                                        <label [for]="'source-warehouse-' + $index">Source Warehouse</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            }
                            <div class="field col-12 md:col-6 lg:col-3">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-circle-check"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        <p-select [id]="'item-condition-' + $index" [options]="conditionOptions"
                                            [(ngModel)]="line.conditionId" [ngModelOptions]="{standalone: true}"
                                            optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code"
                                            [showClear]="true" appendTo="body" (ngModelChange)="refreshIntakePreviews()"
                                            [disabled]="isExecutionLocked() || !line.inventoryItemId" styleClass="w-full">
                                        </p-select>
                                        <label [for]="'item-condition-' + $index">Condition</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            @if (line.inventoryItemId) {
                            <div class="field col-12">
                                <div class="warehouse-item-snapshot">
                                    <div><span>Item code</span><strong>{{ line.warehouseItemNumber || '-' }}</strong></div>
                                    <div><span>SKU</span><strong>{{ line.skuNumber || '-' }}</strong></div>
                                    <div><span>Customer code</span><strong>{{ line.customerItemNumber || '-' }}</strong></div>
                                    <div><span>Tracking</span><strong>{{ line.trackingMode || 'QUANTITY' }}</strong></div>
                                    <div><span>Unit weight</span><strong>{{ selectedWarehouseItem(line)?.weightKg ?? line.itemWeight ?? '-' }} kg</strong></div>
                                    <div><span>Dimensions</span><strong>{{ warehouseItemDimensions(line) }}</strong></div>
                                    <div class="allocation-owner"><span>Allocation</span><strong>Warehouse controlled</strong></div>
                                </div>
                            </div>
                            }
                            } @else {
                            <div class="field col-12 lg:col-4">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-box"></i></p-inputGroupAddon>
                                    <p-iftaLabel>
                                        @if ($index === 0) {
                                        <p-select [id]="'cargo-type-' + $index" [options]="payloadTypes" [(ngModel)]="line.itemType"
                                            (ngModelChange)="refreshIntakePreviews()" optionLabel="name" [filter]="true"
                                            filterBy="name" [showClear]="true" formControlName="payloadTypeDropdown"
                                            [disabled]="isExecutionLocked()" styleClass="w-full">
                                        </p-select>
                                        } @else {
                                        <p-select [id]="'cargo-type-' + $index" [options]="payloadTypes" [(ngModel)]="line.itemType"
                                            [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()"
                                            optionLabel="name" [filter]="true" filterBy="name" [showClear]="true"
                                            [disabled]="isExecutionLocked()" styleClass="w-full">
                                        </p-select>
                                        }
                                        <label [for]="'cargo-type-' + $index">Cargo Type</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            }

                            @if (!isContainerLine(line)) {
                            <div class="field col-12 cargo-measure-field" [ngClass]="isWarehouseItemLine(line) ? 'lg:col-6' : 'lg:col-4'">
                                <div class="cargo-measure-pair">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
                                        <p-iftaLabel class="measure-value">
                                            @if ($index === 0) {
                                            <p-inputNumber [id]="'quantity-' + $index" [(ngModel)]="line.itemQuantity!!.quantity"
                                                (ngModelChange)="onCargoQuantityChange(line, $index)"
                                                [showButtons]="true" formControlName="uomQuantityInput"
                                                [disabled]="isExecutionLocked()" styleClass="w-full">
                                            </p-inputNumber>
                                            } @else {
                                            <p-inputNumber [id]="'quantity-' + $index" [(ngModel)]="line.itemQuantity!!.quantity"
                                                [ngModelOptions]="{standalone: true}" (ngModelChange)="onCargoQuantityChange(line, $index)"
                                                [showButtons]="true"
                                                [disabled]="isExecutionLocked()" styleClass="w-full">
                                            </p-inputNumber>
                                            }
                                            <label [for]="'quantity-' + $index">Quantity</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-scale-balanced"></i></p-inputGroupAddon>
                                        <p-iftaLabel class="measure-unit">
                                            <p-select [id]="'quantity-uom-' + $index" [options]="isWarehouseItemLine(line) ? warehouseItemUoms(line) : uomList" [(ngModel)]="line.itemQuantity!!.uom"
                                                [ngModelOptions]="{standalone: true}" (ngModelChange)="isWarehouseItemLine(line) ? onWarehouseUomChange(line, $index) : refreshIntakePreviews()"
                                                optionLabel="name" [filter]="true" filterBy="name,shortName" [disabled]="isExecutionLocked()" styleClass="w-full">
                                            </p-select>
                                            <label [for]="'quantity-uom-' + $index">UOM</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                @if (warehouseItemUomMessage(line); as uomMessage) {
                                <small class="warehouse-item-uom-message"><i class="fa-solid fa-circle-info"></i>{{ uomMessage }}</small>
                                }
                            </div>
                            <div class="field col-12 cargo-measure-field" [ngClass]="isWarehouseItemLine(line) ? 'lg:col-6' : 'lg:col-4'">
                                <div class="cargo-measure-pair">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-weight-hanging"></i></p-inputGroupAddon>
                                        <p-iftaLabel class="measure-value">
                                            @if ($index === 0) {
                                            <p-inputNumber [id]="'totalWeight-' + $index" [(ngModel)]="cargoTotalWeights[$index]"
                                                (ngModelChange)="refreshIntakePreviews()"
                                                formControlName="itemWeightInput"
                                                [disabled]="isExecutionLocked() || isWarehouseItemLine(line)" styleClass="w-full">
                                            </p-inputNumber>
                                            } @else {
                                            <p-inputNumber [id]="'totalWeight-' + $index" [(ngModel)]="cargoTotalWeights[$index]"
                                                [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()"
                                                [disabled]="isExecutionLocked() || isWarehouseItemLine(line)" styleClass="w-full">
                                            </p-inputNumber>
                                            }
                                            <label [for]="'totalWeight-' + $index">Total Weight</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-ruler-combined"></i></p-inputGroupAddon>
                                        <p-iftaLabel class="measure-unit">
                                            <p-select [id]="'weight-unit-' + $index" [options]="weightUnitList" [(ngModel)]="line.weightUnit"
                                                [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()"
                                                optionLabel="shortName" [filter]="true" filterBy="shortName,fullName"
                                                [disabled]="isExecutionLocked() || isWarehouseItemLine(line)" styleClass="w-full">
                                            </p-select>
                                            <label [for]="'weight-unit-' + $index">Unit</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                            </div>
                            @if (!isWarehouseItemLine(line)) {
                            <div class="field col-12 cargo-notes-field">
                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="fa-solid fa-note-sticky"></i></p-inputGroupAddon>
                                    <p-iftaLabel class="cargo-notes-label">
                                        <input [id]="'cargo-notes-' + $index" pInputText [(ngModel)]="line.itemDesciption"
                                            [ngModelOptions]="{standalone: true}" (ngModelChange)="refreshIntakePreviews()"
                                            [disabled]="isExecutionLocked()" class="w-full" />
                                        <label [for]="'cargo-notes-' + $index">Handling Notes</label>
                                    </p-iftaLabel>
                                </p-inputGroup>
                            </div>
                            }
                            }
                        </div>
                    </div>
                    }
                    <p-button label="Add Cargo Line" icon="fa-solid fa-plus" severity="secondary" [outlined]="true" (onClick)="addCargoLine()" [disabled]="isExecutionLocked()"></p-button>
                </app-page-form-section>

                <app-page-form-section
                    icon="fa-solid fa-truck"
                    title="Equipment"
                    subtitle="Requested trucks are required. Vehicle preferences are optional and will guide delivery assignment.">
                    <div class="formgrid grid">
                        <div class="field col-12 md:col-3">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-truck-front"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="vehicle-type" [options]="vehicleTypes" [(ngModel)]="shipment.vehicleParaPack.vehicleType"
                                        (ngModelChange)="vehicleTypeSelected(); refreshIntakePreviews()" optionLabel="name"
                                        formControlName="vehicleTypeDropdown" [showClear]="true" [disabled]="isExecutionLocked()" styleClass="w-full">
                                    </p-select>
                                    <label for="vehicle-type">Vehicle Type (optional)</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-3">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-sliders"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="vehicle-config" [options]="vehicleConfigurations" [(ngModel)]="shipment.vehicleParaPack.vehicleConfig"
                                        (ngModelChange)="refreshContractPricingDecision(); refreshIntakePreviews()" optionLabel="name" formControlName="vehicleConfigDropdown"
                                        [showClear]="true" [disabled]="isExecutionLocked()" styleClass="w-full">
                                    </p-select>
                                    <label for="vehicle-config">Configuration (optional)</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-3">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-gauge-high"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-select id="vehicle-capacity" [options]="vehicleCapacities" [(ngModel)]="shipment.vehicleParaPack.vehicleCapacity"
                                        (ngModelChange)="refreshContractPricingDecision(); refreshIntakePreviews()" optionLabel="name" formControlName="vehicleCapacityDropdown"
                                        [showClear]="true" [disabled]="isExecutionLocked()" styleClass="w-full">
                                    </p-select>
                                    <label for="vehicle-capacity">Capacity (optional)</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                        <div class="field col-12 md:col-3">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-truck-fast"></i></p-inputGroupAddon>
                                <p-iftaLabel>
                                    <p-inputNumber id="quantityRequired" [(ngModel)]="shipment.quantityRequired" [showButtons]="true"
                                        (ngModelChange)="refreshIntakePreviews()" formControlName="quantityRequiredInput"
                                        [disabled]="isExecutionLocked()" styleClass="w-full">
                                    </p-inputNumber>
                                    <label for="quantityRequired">Requested Trucks</label>
                                </p-iftaLabel>
                            </p-inputGroup>
                        </div>
                    </div>
                </app-page-form-section>

                @if (canViewShipmentPricing()) {
                <app-page-form-section
                    icon="fa-solid fa-money-check-dollar"
                    title="Commercials"
                    subtitle="Set the customer selling price. Transport buying and internal cost are captured when deliveries are assigned.">
                    <div class="commercial-panel">
                        <!-- Operational cost is intentionally captured per delivery assignment. -->
                        @if (false) {
                        <div class="commercial-subsection">
                            <div class="commercial-subheader">
                                <div>
                                    <strong>Buying Cost Source</strong>
                                    <span>Choose how the carrier cost should be defaulted.</span>
                                </div>
                                <span [class]="sourceBadgeClass(buyingSourceBadgeLabel())">{{ buyingSourceBadgeLabel() }}</span>
                            </div>
                            <div class="formgrid grid">
                                <div class="field col-12 md:col-3">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-coins"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="buying-cost-source" [options]="buyingCostSourceOptions" [(ngModel)]="buyingCostSource"
                                                [ngModelOptions]="{standalone: true}" optionLabel="label" optionValue="value"
                                                (ngModelChange)="onBuyingCostSourceSelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="buying-cost-source">Buying Cost Source</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-3">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-money-bill-wave"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="currency" [options]="currencyOptions" [(ngModel)]="currencyCode" [ngModelOptions]="{standalone: true}"
                                                optionLabel="label" optionValue="value" (ngModelChange)="refreshIntakePreviews()" styleClass="w-full">
                                            </p-select>
                                            <label for="currency">Currency</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                @if (isSisterFleetBuyingSource()) {
                                <div class="field col-12 md:col-6">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-building-user"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="sister-company" [options]="sisterCompanyScope?.sisterCompanies || []" [(ngModel)]="selectedSisterCompany"
                                                [ngModelOptions]="{standalone: true}" optionLabel="name" [filter]="true" filterBy="name"
                                                [showClear]="true" (ngModelChange)="onSisterCompanySelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="sister-company">Sister Company</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                }
                                @if (isSupplierBuyingSource()) {
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-address-book"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="supplier-client" [options]="supplierClients()" [(ngModel)]="selectedSupplierClient"
                                                [ngModelOptions]="{standalone: true}" optionLabel="displayLabel" [filter]="true" filterBy="displayLabel,companyName,clientNumber"
                                                [showClear]="true" [loading]="supplierClientsLoading" (onShow)="loadSupplierClients()"
                                                (ngModelChange)="onSupplierClientSelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="supplier-client">Supplier CRM Client</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-file-contract"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="supplier-contract" [options]="supplierContractOptions" [(ngModel)]="selectedSupplierContract"
                                                [ngModelOptions]="{standalone: true}" optionLabel="contractNumber" [filter]="true" filterBy="contractNumber"
                                                [showClear]="true" [loading]="supplierContractOptionsLoading" [virtualScroll]="true" [virtualScrollItemSize]="38"
                                                (onShow)="loadSupplierContractOptions()" (onLazyLoad)="loadSupplierContractOptions($event)"
                                                (ngModelChange)="onSupplierContractSelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="supplier-contract">Supplier Contract</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-tags"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="supplier-rate-card" [options]="supplierRateCardOptions" [(ngModel)]="selectedSupplierRateCard"
                                                [ngModelOptions]="{standalone: true}" optionLabel="displayLabel" [filter]="true" filterBy="displayLabel"
                                                [showClear]="true" [loading]="supplierRateCardOptionsLoading" [virtualScroll]="true" [virtualScrollItemSize]="38"
                                                (onShow)="loadSupplierRateCardOptions()" (ngModelChange)="onSupplierRateCardSelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="supplier-rate-card">Supplier Rate Card</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                }
                                @if (isEnabledEnterpriseFeatures()) {
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-arrow-down"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-inputNumber id="buyingPrice" [ngModel]="operationalCostAmount()" (ngModelChange)="onOperationalCostChanged($event)"
                                                formControlName="buyingPriceInput" mode="currency" [currency]="currencyCode" styleClass="w-full">
                                            </p-inputNumber>
                                            <label for="buyingPrice">{{ operationalCostFieldLabel() }}</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                }
                                <div class="field col-12 md:col-4">
                                    <div class="margin-card compact-card">
                                        <span>{{ recommendedCostLabel() }}</span>
                                        <strong>{{ (lastRecommendedBuyingPrice || 0) | number:'1.2-2' }} {{ currencyCode }}</strong>
                                        <small>{{ pricingPreview?.buyingPriceSource || 'Awaiting route and equipment match' }}</small>
                                    </div>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <div class="margin-card compact-card">
                                        <span>Waiting Terms</span>
                                        <strong>{{ pricingPreview?.internalCostWaitingThresholdMinutes || 0 }} min / {{ pricingPreview?.internalCostWaitingChargeAmount || 0 | number:'1.2-2' }} {{ internalCostCurrencyCode() }}</strong>
                                        <small *ngIf="pricingPreview?.internalCostCardId">Internal card #{{ pricingPreview?.internalCostCardId }}</small>
                                    </div>
                                </div>
                                @if (buyingWarning()) {
                                <div class="field col-12">
                                    <div class="commercial-alert">
                                        <i class="fa-solid fa-triangle-exclamation"></i>
                                        <span>{{ buyingWarning() }}</span>
                                    </div>
                                </div>
                                }
                            </div>
                        </div>
                        }

                        <div class="commercial-subsection">
                            <div class="commercial-subheader">
                                <div>
                                    <strong>Customer Selling Source</strong>
                                    <span>The matching contract revision and rate card set the selling price.</span>
                                </div>
                                <span [class]="sourceBadgeClass(sellingSourceBadgeLabel())">{{ sellingSourceBadgeLabel() }}</span>
                            </div>
                            <div class="formgrid grid">
								<div class="field col-12 md:col-4">
									<p-inputGroup>
										<p-inputGroupAddon><i class="fa-solid fa-receipt"></i></p-inputGroupAddon>
										<p-iftaLabel>
											<p-select id="payment-term" [options]="paymentTerms" [(ngModel)]="paymentTermId"
												[ngModelOptions]="{standalone: true}" optionLabel="name" optionValue="paymentTermID"
												[filter]="true" [showClear]="false" (ngModelChange)="refreshIntakePreviews()" styleClass="w-full">
											</p-select>
											<label for="payment-term">Commercial Payment Term *</label>
										</p-iftaLabel>
									</p-inputGroup>
								</div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-money-bill-wave"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="currency" [options]="currencyOptions" [(ngModel)]="currencyCode" [ngModelOptions]="{standalone: true}"
                                                optionLabel="label" optionValue="value" (ngModelChange)="refreshIntakePreviews()" styleClass="w-full">
                                            </p-select>
                                            <label for="currency">Currency</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-handshake"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="rate-source" [options]="rateSourceOptions" [(ngModel)]="rateSource" [ngModelOptions]="{standalone: true}"
                                                optionLabel="label" optionValue="value" (ngModelChange)="onSellingRateSourceSelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="rate-source">Customer Selling Source</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-file-signature"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="contract-id" [options]="contractOptions" [(ngModel)]="selectedPricingContract"
                                                [ngModelOptions]="{standalone: true}" optionLabel="contractNumber" [filter]="true" filterBy="contractNumber"
                                                [showClear]="true" [loading]="contractOptionsLoading" [virtualScroll]="true" [virtualScrollItemSize]="38"
                                                (onShow)="loadContractOptions()" (onLazyLoad)="loadContractOptions($event)"
                                                (ngModelChange)="onPricingContractSelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="contract-id">Customer Contract</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-tag"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="rate-card-id" [options]="rateCardOptions" [(ngModel)]="selectedPricingRateCard"
                                                [ngModelOptions]="{standalone: true}" optionLabel="displayLabel" [filter]="true" filterBy="displayLabel"
                                                [showClear]="true" [loading]="rateCardOptionsLoading" [virtualScroll]="true" [virtualScrollItemSize]="38"
                                                (onShow)="loadRateCardOptions()" (ngModelChange)="onPricingRateCardSelected($event)" styleClass="w-full">
                                            </p-select>
                                            <label for="rate-card-id">Customer Rate Card</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-code-branch"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-select id="contract-revision" [options]="selectedPricingContract?.revisions ?? []"
                                                [(ngModel)]="pricingContractRevisionId" [ngModelOptions]="{standalone: true}"
                                                optionLabel="revisionNumber" optionValue="revisionID" [showClear]="true"
                                                [disabled]="!selectedPricingContract" (ngModelChange)="onPricingRevisionSelected($event)" styleClass="w-full">
                                                <ng-template let-revision pTemplate="item">Revision {{ revision.revisionNumber }} ({{ revision.status }})</ng-template>
                                                <ng-template let-revision pTemplate="selectedItem">Revision {{ revision.revisionNumber }}</ng-template>
                                            </p-select>
                                            <label for="contract-revision">Contract Revision</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-arrow-up"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <p-inputNumber id="sellingPrice" [(ngModel)]="shipment.sellingPrice" (ngModelChange)="refreshIntakePreviews()"
                                                formControlName="sellingPriceInput" mode="currency" [currency]="currencyCode" styleClass="w-full">
                                            </p-inputNumber>
                                            <label for="sellingPrice">Selling Price</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <div class="margin-card compact-card">
                                        <span>Transport Cost</span>
                                        <strong>Set at delivery assignment</strong>
                                        <small>Buying/internal cost is not shared across deliveries.</small>
                                    </div>
                                </div>
                                <div class="field col-12 md:col-4">
                                    <p-inputGroup>
                                        <p-inputGroupAddon><i class="fa-solid fa-comment-dots"></i></p-inputGroupAddon>
                                        <p-iftaLabel>
                                            <input id="overrideReason" pInputText [(ngModel)]="manualOverrideReason" [ngModelOptions]="{standalone: true}"
                                                (ngModelChange)="refreshIntakePreviews()" />
                                            <label for="overrideReason">Manual Override Reason</label>
                                        </p-iftaLabel>
                                    </p-inputGroup>
                                    @if (requiresManualOverrideReason() && !manualOverrideReason) {
                                    <small class="field-warning">Required when the customer selling rate is overridden.</small>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </app-page-form-section>
                }

                <app-page-form-section
                    icon="fa-solid fa-clipboard-check"
                    title="Documents & Compliance"
                    subtitle="Show readiness blockers before dispatch, POD, and finance workflows consume the shipment.">
                    <div class="document-upload-card">
                        <div class="document-upload-header">
                            <div>
                                <strong>Shipment Documents</strong>
                                <span>Attach cargo evidence, customer references, rate sheets, and compliance files.</span>
                            </div>
                            <input #shipmentDocumentInput type="file" multiple class="document-file-input"
                                (change)="onShipmentDocumentsSelected($event)" [disabled]="isExecutionLocked()" />
                            <p-button label="Attach Documents" icon="fa-solid fa-paperclip" severity="secondary" [outlined]="true"
                                (onClick)="shipmentDocumentInput.click()" [disabled]="isExecutionLocked() || shipmentCreationInProgress"></p-button>
                        </div>
                        @if (shipmentLevelAttachments().length || shipmentDocumentQueue.length) {
                        <div class="document-file-list">
                            @for (file of shipmentLevelAttachments(); track file.attachmentId || file.fileUrl) {
                            <div class="document-file-row">
                                <i class="fa-solid fa-file-lines"></i>
                                <div>
                                    <strong>{{ file.fileName }}</strong>
                                    <span>{{ formatFileSize(file.fileSize) }} | Attached</span>
                                </div>
                            </div>
                            }
                            @for (document of shipmentDocumentQueue; track document.file.name + document.file.size + document.file.lastModified; let documentIndex = $index) {
                            <div class="document-file-row">
                                <i class="fa-solid fa-file-arrow-up"></i>
                                <div>
                                    <strong>{{ document.file.name }}</strong>
                                    <span>{{ formatFileSize(document.file.size) }} | {{ document.state === 'queued' ? 'Ready to upload' : (document.progress + '%') }}</span>
                                </div>
                                <p-button icon="pi pi-times" severity="secondary" [text]="true"
                                    (onClick)="removeQueuedShipmentDocument(documentIndex)"
                                    [disabled]="shipmentCreationInProgress"></p-button>
                            </div>
                            }
                        </div>
                        } @else {
                        <div class="document-empty-state">
                            No documents selected yet.
                        </div>
                        }
                    </div>
                    <div class="document-grid">
                        @for (item of documentChecklist(); track item.label) {
                        <div class="document-item" [class.is-success]="item.severity === 'success'" [class.is-danger]="item.severity === 'danger'">
                            <span>{{ item.label }}</span>
                            <strong>{{ item.status }}</strong>
                        </div>
                        }
                    </div>
                </app-page-form-section>

                <app-page-form-section
                    icon="fa-solid fa-note-sticky"
                    title="Shipment Notes"
                    subtitle="Add operational context, customer instructions, or follow-up details for this shipment.">
                    <div class="formgrid grid">
                        <div class="field col-12 mb-0">
                            <p-iftaLabel>
                                <textarea id="shipment-notes" pTextarea [(ngModel)]="shipmentNotes" [ngModelOptions]="{standalone: true}"
                                    rows="5" class="w-full" (ngModelChange)="refreshIntakePreviews()"></textarea>
                                <label for="shipment-notes">Shipment Notes</label>
                            </p-iftaLabel>
                        </div>
                    </div>
                </app-page-form-section>
            </div>

            <aside class="shipment-readiness-aside">
                <div class="readiness-panel">
                    <div class="readiness-panel-header">
                        <span>Review & Create</span>
                        <strong>{{ readinessPercent() }}%</strong>
                    </div>
                    <p-progressBar [value]="readinessPercent()"></p-progressBar>

                    @if (submissionIssues.length > 0) {
                    <section class="submission-error-panel" aria-live="assertive" aria-label="Shipment save errors">
                        <div class="submission-error-panel-header">
                            <i class="fa-solid fa-circle-exclamation"></i>
                            <div>
                                <strong>Unable to create shipment</strong>
                                <span>Resolve the following before trying again.</span>
                            </div>
                        </div>
                        <ul>
                            @for (issue of submissionIssues; track issue) {
                            <li>{{ issue }}</li>
                            }
                        </ul>
                    </section>
                    }

                    @if (readinessPercent() === 100 && intakeFormBlockingIssues().length > 0) {
                    <section class="submission-error-panel" aria-live="polite" aria-label="Fields blocking shipment creation">
                        <div class="submission-error-panel-header">
                            <i class="fa-solid fa-circle-exclamation"></i>
                            <div><strong>Shipment cannot be created yet</strong></div>
                        </div>
                        <ul>
                            @for (issue of intakeFormBlockingIssues(); track issue) {
                            <li>{{ issue }}</li>
                            }
                        </ul>
                    </section>
                    }

                    <div class="readiness-status-list">
                        <div [class.is-ready]="intakeReadiness?.partiesReady"><span>Parties</span><strong>{{ intakeReadiness?.partiesReady ? 'Ready' : 'Missing' }}</strong></div>
                        <div [class.is-ready]="intakeReadiness?.routeReady"><span>Route</span><strong>{{ intakeReadiness?.routeReady ? 'Ready' : 'Missing' }}</strong></div>
                        <div [class.is-ready]="intakeReadiness?.cargoReady"><span>Cargo</span><strong>{{ intakeReadiness?.cargoReady ? 'Ready' : 'Missing' }}</strong></div>
                        <div [class.is-ready]="intakeReadiness?.equipmentReady"><span>Equipment</span><strong>{{ intakeReadiness?.equipmentReady ? 'Ready' : 'Missing' }}</strong></div>
                        @if (canViewShipmentPricing()) {
                        <div [class.is-ready]="intakeReadiness?.pricingReady"><span>Pricing</span><strong>{{ intakeReadiness?.pricingReady ? 'Ready' : 'Missing' }}</strong></div>
                        }
                    </div>

                    <div class="aside-route">
                        <span>Route Preview</span>
                        <strong>{{ routePreview?.distanceKm || 0 }} km</strong>
                        <small>{{ routePreview?.durationSeconds ? formatDurationHours(routePreview?.durationSeconds) : 'Duration pending' }}</small>
                    </div>

                    @if ((intakeReadiness?.issues?.length || 0) > 0) {
                    <div class="intake-message-list stacked">
                        @for (issue of intakeReadiness?.issues; track issue) {
                        <span class="intake-issue"><i class="fa-solid fa-circle-exclamation"></i>{{ issue }}</span>
                        }
                    </div>
                    }
                    @if ((intakeReadiness?.warnings?.length || 0) > 0 || pricingWarnings.length > 0) {
                    <div class="intake-message-list stacked">
                        @for (warning of intakeReadiness?.warnings; track warning) {
                        <span class="intake-warning"><i class="fa-solid fa-triangle-exclamation"></i>{{ warning }}</span>
                        }
                        @for (warning of pricingWarnings; track warning) {
                        <span class="intake-warning"><i class="fa-solid fa-triangle-exclamation"></i>{{ warning }}</span>
                        }
                    </div>
                    }

                    <p-button styleClass="w-full mb-2" label="Create Shipment & Assign Drivers" [loading]="shipmentCreationInProgress"
                        icon="fa-solid fa-truck-fast" severity="secondary" [outlined]="true" (onClick)="submitOrder(true)" [disabled]="!canSubmitIntake()"></p-button>
                    <p-button styleClass="w-full" label="Create Shipment" [loading]="shipmentCreationInProgress"
                        icon="fa-solid fa-check" (onClick)="submitOrder()" [disabled]="!canSubmitIntake()"></p-button>
                </div>
            </aside>
        </div>
    </form>
</app-page-shell>

<app-customer-form
    [displayModal]="showCustomerForm"
    [dialogHeader]="customerFormHeader"
    [submitButtonLabel]="customerFormSubmitLabel"
    [company]="selectedCompany"
    [customer]="selectedCustomer"
    (modalClosed)="onCustomerFormClosed($event)">
</app-customer-form>

<app-company-form
    [displayModal]="showCompanyForm"
    [dialogHeader]="companyFormHeader"
    [submitButtonLabel]="companyFormSubmitLabel"
    [company]="selectedCompany"
    (modalClosed)="onCompanyFormClosed()">
</app-company-form>
